import { useRouter } from '@tanstack/react-router';
import { type JSX, type ReactNode, useEffect } from 'react';

import {
  type ShellPlugins,
  applyStatusBar,
  hideSplash,
  onBackButton,
} from '@/lib/native/shell';

/**
 * BP-11 §6's app shell row: "status-bar colour, safe-area insets, back-button
 * handling (Android), splash hide after first render".
 *
 * It renders its children and nothing else. The three native behaviours are
 * side effects, and all three are no-ops in a browser — which is why this
 * component wraps the *client PWA* too rather than being a native-only
 * branch. One tree, tested once.
 *
 * The safe-area insets are the fourth, and they are CSS, not JavaScript:
 * `index.html` carries `viewport-fit=cover` so `env(safe-area-inset-*)`
 * resolves to something, and the padding below applies it. A notched iPhone
 * puts the clock over the top 47 px and the home indicator over the bottom
 * 34; without this the header sits under the clock.
 */
export function NativeShell({
  children,
  plugins,
}: {
  children: ReactNode;
  plugins?: ShellPlugins;
}): JSX.Element {
  // `useRouter()` is typed against the registered route tree, which this
  // repository does not declare, so `history` arrives as `any`. The two
  // methods used here are named rather than cast wholesale.
  const history = useRouter().history as {
    back: () => void;
    canGoBack: () => boolean;
  };

  useEffect(() => {
    void applyStatusBar(plugins);
  }, [plugins]);

  useEffect(() => {
    // After the first paint, not on a timer (`launchAutoHide: false`): a
    // splash that disappears while the first render is still blank reads as
    // a crash.
    void hideSplash(plugins);
  }, [plugins]);

  useEffect(() => {
    let dispose = (): void => undefined;
    let disposed = false;
    void onBackButton(
      () => {
        history.back();
      },
      () => history.canGoBack(),
      plugins,
    ).then((off) => {
      // The listener resolves asynchronously, so an unmount can land first.
      // Without this the handler outlives the component and the next back
      // press navigates a tree that is gone.
      if (disposed) {
        off();
        return;
      }
      dispose = off;
    });
    return () => {
      disposed = true;
      dispose();
    };
  }, [history, plugins]);

  return (
    <div
      data-testid="native-shell"
      className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
    >
      {children}
    </div>
  );
}
